import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { APP_ROUTES } from '@/config/routes.config';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface AppSidebarProps {
  collapsed?: boolean;
  onNavigate?: () => void;
}

export const AppSidebar = ({ collapsed = false, onNavigate }: AppSidebarProps) => {
  const location = useLocation();
  const { user } = useAuth();
  const { can } = usePermissions();
  const { canAccess } = useFeatureAccess();

  // Filter routes based on permissions and features
  const filteredRoutes = useMemo(() => {
    return APP_ROUTES.filter(route => {
      // Check role requirements
      if (route.requiresRole && user?.roles) {
        const hasRole = route.requiresRole.some(role => user.roles?.includes(role));
        if (!hasRole) return false;
      }

      // Check feature requirements
      if (route.requiresFeature) {
        const access = canAccess(route.requiresFeature, 'view');
        // Show with lock icon if gated
        return true;
      }

      return true;
    });
  }, [user, canAccess]);

  // Group routes
  const mainRoutes = filteredRoutes.filter(r => r.group === 'main' || !r.group);
  const accountRoutes = filteredRoutes.filter(r => r.group === 'account');

  const renderNavItem = (route: typeof APP_ROUTES[0]) => {
    const isActive = location.pathname === route.path || location.pathname.startsWith(route.path + '/');
    const IconComponent = (LucideIcons as any)[route.icon] || LucideIcons.Circle;
    
    // Check if feature is gated
    const isGated = route.requiresFeature && !canAccess(route.requiresFeature, 'view').enabled;

    const navItem = (
      <Link
        to={route.path}
        onClick={onNavigate}
        aria-label={route.label}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          "flex items-center gap-3 px-3 py-2 rounded-lg transition-all",
          "hover:bg-accent/10 focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none",
          isActive && "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100",
          !isActive && "text-muted-foreground hover:text-foreground",
          collapsed && "justify-center px-2"
        )}
      >
        <IconComponent 
          className={cn("h-5 w-5 flex-shrink-0", isActive && "text-emerald-600 dark:text-emerald-400")} 
          aria-hidden="true"
        />
        {!collapsed && (
          <>
            <span className="flex-1 text-sm font-medium">{route.label}</span>
            {isGated && (
              <Lock className="h-3 w-3 text-muted-foreground" aria-label="Premium feature" />
            )}
          </>
        )}
      </Link>
    );

    if (collapsed) {
      return (
        <TooltipProvider key={route.path}>
          <Tooltip>
            <TooltipTrigger asChild>
              {navItem}
            </TooltipTrigger>
            <TooltipContent side="right" className="flex items-center gap-2">
              <span>{route.label}</span>
              {isGated && <Lock className="h-3 w-3" />}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return <div key={route.path}>{navItem}</div>;
  };

  return (
    <nav 
      className="flex-1 overflow-y-auto p-3 space-y-6" 
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Main Navigation */}
      <div className="space-y-1" role="group" aria-label="Main menu">
        {!collapsed && (
          <h3 
            className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
            id="main-nav-heading"
          >
            Main
          </h3>
        )}
        <div role="menu" aria-labelledby={collapsed ? undefined : "main-nav-heading"}>
          {mainRoutes.map(renderNavItem)}
        </div>
      </div>

      {/* Account Section */}
      {accountRoutes.length > 0 && (
        <div className="space-y-1" role="group" aria-label="Account menu">
          {!collapsed && (
            <h3 
              className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
              id="account-nav-heading"
            >
              Account
            </h3>
          )}
          <div role="menu" aria-labelledby={collapsed ? undefined : "account-nav-heading"}>
            {accountRoutes.map(renderNavItem)}
          </div>
        </div>
      )}

      {/* Admin Link (if user has admin role) */}
      {user?.profile?.is_super_admin && (
        <div className="space-y-1 pt-3 border-t" role="group" aria-label="Admin menu">
          {!collapsed && (
            <h3 
              className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
              id="admin-nav-heading"
            >
              Admin
            </h3>
          )}
          <Link
            to="/admin"
            aria-label="Admin Portal"
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg transition-all",
              "text-muted-foreground hover:text-foreground hover:bg-accent/10",
              "focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none",
              collapsed && "justify-center px-2"
            )}
          >
            <LucideIcons.Shield className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            {!collapsed && <span className="text-sm font-medium">Admin Portal</span>}
          </Link>
        </div>
      )}
    </nav>
  );
};
